import { useState } from 'react';
import styled from 'styled-components';
import { Box, Divider, Heading, Paragraph, Stack } from '@99designs/emu';
import { useContestAndFixedPriceProjectProductsConfig } from '../../util/useContestAndFixedPriceProjectProductsConfig';
import { BaseLayout } from '../BaseLayout';
import { Hero } from '../Hero';
import { HowItWorksTabbed } from '../HowItWorksTabbed';
import { PackageInclusions } from '../PackageInclusions';
import { ProductGroupPreview } from '../ProductGroupPreview';
import { ProductGroupSummary } from '../ProductGroupSummary';
import { STICKY_BAR_HEIGHT_PX, StickyBar } from '../StickyBar';
import { DEFAULT_SCROLL_OFFSET_PX, TabId, TabsMenu, TabsMenuDivider, navTabs } from '../TabsMenu';
import type { ZendeskLanguage } from '@99designs/routing';
import type {
    ComparativeProduct,
    ContestProduct,
    FixedPriceProjectProduct,
    ProductGroup,
    QuestionAndAnswer,
    RelatedProduct,
} from '../../types';
import type { InspirationProps } from '../BaseLayout/BaseLayout';

export type Props = {
    productGroup: ProductGroup;
    products: ContestProduct[] | FixedPriceProjectProduct[];
    inspiration?: InspirationProps;
    relatedProducts?: RelatedProduct[];
    faqs: QuestionAndAnswer[];
    lang: ZendeskLanguage;
};

const RightColumnContainer = styled(Box)<{ hasStickyBar: boolean }>`
    padding-top: ${({ hasStickyBar }) => (hasStickyBar ? STICKY_BAR_HEIGHT_PX : 0)}px;
`;

export function ContestFixedPriceProjectLayout({
    productGroup,
    products,
    inspiration,
    relatedProducts,
    faqs,
    lang,
}: Props) {
    const [hasStickyBar, setHasStickyBar] = useState<boolean>(false);
    const productsSortedByPrice = sortProductsByPrice(products);

    const comparativeProducts: ComparativeProduct[] =
        useContestAndFixedPriceProjectProductsConfig(products);

    const tabs = navTabs();

    return (
        <BaseLayout
            stickyColumnPosition="right"
            moneyBackGuarantee
            inspiration={inspiration}
            relatedProducts={relatedProducts}
            comparativeProducts={comparativeProducts}
            faqs={
                faqs.length > 0
                    ? { title: productGroup.attributes.title || '', body: faqs }
                    : undefined
            }
            leftColumn={
                <>
                    <Hero images={productGroup.attributes.hero} />

                    <Box
                        display={['none', 'none', 'block', 'block']}
                        paddingTop={['none', 'none', '4bu', '4bu']}
                        paddingBottom={['none', '3bu', '3bu', '3bu']}
                    >
                        <StickyBar onStickyTriggered={setHasStickyBar}>
                            {/* We assume that sticky bar is always shown when tabs are used */}
                            <TabsMenu
                                tabs={tabs}
                                scrollOffsetTop={DEFAULT_SCROLL_OFFSET_PX + STICKY_BAR_HEIGHT_PX}
                            />
                            <TabsMenuDivider />
                        </StickyBar>
                    </Box>

                    <Box display={['none', 'none', 'block', 'block']}>
                        <Stack space="0.75bu">
                            <Heading appearanceLevel={4} accessibilityLevel={2} as="p">
                                {productGroup.attributes.subtitle}
                            </Heading>
                            <Paragraph>{productGroup.attributes.description}</Paragraph>
                        </Stack>
                    </Box>

                    <Box
                        display={['none', 'none', 'block', 'block']}
                        paddingTop={['none', '3bu', '3bu', '3bu']}
                    >
                        <Divider />
                    </Box>

                    <Box
                        display={['block', 'block', 'none', 'none']}
                        paddingTop={['2bu', '3bu', 'none', 'none']}
                    >
                        {productsSortedByPrice.length > 0 && (
                            <ProductGroupSummary
                                title={productGroup.attributes.title}
                                price={productsSortedByPrice[0].pricing}
                                subtitle={productGroup.attributes.subtitle}
                                description={productGroup.attributes.description}
                            />
                        )}
                    </Box>

                    <Box paddingTop={['2bu', '3bu', 'none', 'none']}>
                        <Stack space={['none', '3bu', '3bu', '3bu']}>
                            <Box
                                display={['block', 'block', 'none', 'none']}
                                paddingBottom={['3bu', 'none', 'none', 'none']}
                            >
                                <TabsMenu tabs={tabs} />
                                <TabsMenuDivider />
                            </Box>

                            <PackageInclusions
                                inclusions={productGroup.attributes.inclusions || []}
                            />
                            <Box display={['none', 'block', 'block', 'block']}>
                                <Divider />
                            </Box>

                            <Box
                                display={['none', 'block', 'block', 'block']}
                                id={TabId.HowItWorks}
                            >
                                <HowItWorksTabbed />
                            </Box>
                        </Stack>
                    </Box>
                </>
            }
            rightColumn={
                <RightColumnContainer
                    display={['none', 'none', 'block', 'block']}
                    hasStickyBar={hasStickyBar}
                >
                    <ProductGroupPreview
                        title={productGroup.attributes.title}
                        price={productsSortedByPrice[0].pricing}
                        inclusions={productGroup.attributes.inclusions}
                    />
                </RightColumnContainer>
            }
            lang={lang}
        />
    );
}

function sortProductsByPrice(products: ContestProduct[] | FixedPriceProjectProduct[]) {
    return products.sort(
        (product, prevProduct) => product.pricing.amountInCents - prevProduct.pricing.amountInCents
    );
}
